import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { api, HEARD_FROM_LABELS } from '@/lib/api';
import { cn } from '@/lib/cn';
import {
  DateRangePicker,
  computePreset,
  type DatePreset,
  type DateRange,
} from '@/components/DateRangePicker';

export const Route = createFileRoute('/_authed/analytics/')({
  component: AnalyticsDashboardPage,
});

// Module 10 dashboard. Single page so a single-village admin can see
// everything in one screen-scroll. Sub-detail (full suggestions list,
// per-POI rating drill-down) lives in linked routes; this page surfaces
// the headline KPIs and the most-actionable summaries.

function AnalyticsDashboardPage() {
  const [preset, setPreset] = useState<DatePreset>('30d');
  const [range, setRange] = useState<DateRange>(() => computePreset('30d'));
  // '' means every village — the dashboard's historical behaviour, kept as
  // the default so nobody's bookmark changes meaning.
  const [villageId, setVillageId] = useState<string>('');

  const villagesQ = useQuery({
    queryKey: ['villages'],
    queryFn: () => api.listVillages(),
  });

  // One filter object for every query below, so the village and the date
  // window can never drift apart between sections.
  const filter = { ...range, villageId: villageId || undefined };

  const overviewQ = useQuery({
    queryKey: ['analytics', 'overview', filter],
    queryFn: () => api.analyticsOverview(filter),
  });
  const demoQ = useQuery({
    queryKey: ['analytics', 'demographics', filter],
    queryFn: () => api.analyticsDemographics(filter),
  });
  const funnelQ = useQuery({
    queryKey: ['analytics', 'funnel', filter],
    queryFn: () => api.analyticsFunnel(filter),
  });
  const ratingsQ = useQuery({
    queryKey: ['analytics', 'ratings', filter],
    queryFn: () => api.analyticsRatings(filter),
  });
  const redemptionsSummaryQ = useQuery({
    queryKey: ['analytics', 'redemptionsSummary', filter],
    queryFn: () => api.analyticsRedemptionsSummary(filter),
  });

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-h1">Analytics</h1>
        <p className="mt-1 text-body text-slate-gray">
          Live KPIs, demographics, route funnel and partner programme
          performance. Use the date picker to slice everything below.
        </p>
      </header>

      <div className="card flex flex-wrap items-end gap-3">
        <div>
          <label className="label">Village</label>
          <select
            className="input"
            value={villageId}
            onChange={(e) => setVillageId(e.target.value)}
          >
            <option value="">All villages</option>
            {villagesQ.data?.villages.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name.en}
              </option>
            ))}
          </select>
        </div>
        <p className="pb-2 text-caption text-slate-gray">
          Scopes every section below except the CSV exports, which carry their
          own village column.
        </p>
      </div>

      <DateRangePicker
        value={range}
        onChange={setRange}
        preset={preset}
        onPresetChange={setPreset}
      />

      <OverviewKpis overview={overviewQ.data} loading={overviewQ.isLoading} />

      <EntryFunnel overview={overviewQ.data} filter={filter} />

      <section>
        <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-h2">Demographics</h2>
          {/*
            The breakdowns count only visitors who completed the form. Stating
            the coverage here replaces the old "Not provided" bar, which read
            as a refusal rate when it actually counted people who had merely
            scanned a sticker (EXP-11).
          */}
          {demoQ.data ? (
            <p className="text-caption text-slate-gray">
              {demoQ.data.coverage.withProfile.toLocaleString()} of{' '}
              {demoQ.data.coverage.sessions.toLocaleString()} visitors completed
              the form
            </p>
          ) : null}
        </div>
        {demoQ.data ? (
          <div className="grid gap-3 sm:grid-cols-2">
            <DemographicCard
              title="Nationality"
              buckets={demoQ.data.nationality}
              emptyLabel="Not provided"
            />
            <DemographicCard
              title="Age range"
              buckets={demoQ.data.ageRange}
              emptyLabel="Not provided"
            />
            <DemographicCard
              title="Gender"
              buckets={demoQ.data.gender}
              emptyLabel="Not provided"
            />
            <DemographicCard
              title="Preferred language"
              buckets={demoQ.data.language}
              emptyLabel="Not provided"
            />
          </div>
        ) : (
          <div className="card text-slate-gray">Loading demographics…</div>
        )}
      </section>

      <section>
        <h2 className="mb-3 text-h2">How did you hear about us?</h2>
        {demoQ.data ? (
          <div className="grid gap-3 sm:grid-cols-2">
            <DemographicCard
              title="Referral source"
              buckets={demoQ.data.heardFrom}
              emptyLabel="Not answered"
              labelMap={HEARD_FROM_LABELS}
            />
          </div>
        ) : (
          <div className="card text-slate-gray">Loading…</div>
        )}
      </section>

      <section>
        <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-h2">Route progress</h2>
          {/* Export sits next to the section it exports, not only in the CSV
              block at the bottom — this is the table people ask for. */}
          <a
            href={api.routeProgressCsvUrl(filter)}
            className="btn-ghost text-tech-blue"
            download
          >
            ⬇ Export this table
          </a>
        </div>
        {funnelQ.data ? (
          <div className="space-y-4">
            {funnelQ.data.routes.map((r) => (
              <FunnelChart key={r.id} route={r} />
            ))}
            {funnelQ.data.routes.length === 0 ? (
              <div className="card text-slate-gray">No routes configured yet.</div>
            ) : null}
          </div>
        ) : (
          <div className="card text-slate-gray">Loading funnel…</div>
        )}
      </section>

      <section>
        <h2 className="mb-3 text-h2">Per-POI ratings</h2>
        {ratingsQ.data && ratingsQ.data.ratings.length > 0 ? (
          <RatingsTable rows={ratingsQ.data.ratings} />
        ) : (
          <div className="card text-slate-gray">
            {ratingsQ.isLoading ? 'Loading ratings…' : 'No ratings in this window.'}
          </div>
        )}
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="mb-3 text-h2">Top partners</h2>
          {redemptionsSummaryQ.data ? (
            <BusinessSummaryTable rows={redemptionsSummaryQ.data.byBusiness} />
          ) : (
            <div className="card text-slate-gray">Loading…</div>
          )}
        </div>
        <div>
          <h2 className="mb-3 text-h2">Top perks</h2>
          {redemptionsSummaryQ.data ? (
            <PerkSummaryTable rows={redemptionsSummaryQ.data.byPerk} />
          ) : (
            <div className="card text-slate-gray">Loading…</div>
          )}
        </div>
      </section>

      <section>
        <div className="mb-3 flex items-baseline justify-between">
          <h2 className="text-h2">Latest suggestions</h2>
          <Link to="/analytics/suggestions" className="btn-ghost">
            View all →
          </Link>
        </div>
        <LatestSuggestions range={range} />
      </section>

      <section>
        <h2 className="mb-3 text-h2">CSV exports</h2>
        <p className="mb-3 text-caption text-slate-gray">
          Download the raw rows for the selected window. Each CSV uses the
          same date filter as the dashboard above, includes the joined
          English-locale title for readability, and is UTF-8 without BOM
          (Excel and Google Sheets eat it as-is).
        </p>
        <div className="flex flex-wrap gap-3">
          <a
            href={api.csvUrl('redemptions', range)}
            className="btn-secondary"
            download
          >
            ⬇ Redemptions
          </a>
          <a href={api.csvUrl('ratings', range)} className="btn-secondary" download>
            ⬇ Ratings
          </a>
          {/* Aggregated per village rather than raw rows — the one export
              that answers "who comes to this village" (EXP-9). */}
          <a
            href={api.demographicsCsvUrl(filter)}
            className="btn-secondary"
            download
          >
            ⬇ Demographics by village
          </a>
          <a
            href={api.entryFunnelCsvUrl(filter)}
            className="btn-secondary"
            download
          >
            ⬇ Entry funnel by village
          </a>
          <a
            href={api.routeProgressCsvUrl(filter)}
            className="btn-secondary"
            download
          >
            ⬇ Route progress
          </a>
          <a
            href={api.csvUrl('suggestions', range)}
            className="btn-secondary"
            download
          >
            ⬇ Suggestions
          </a>
        </div>
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Overview KPI grid — five cards, sized to fit one screen-row at desktop
// ---------------------------------------------------------------------------

function OverviewKpis({
  overview,
  loading,
}: {
  overview?: Awaited<ReturnType<typeof api.analyticsOverview>>;
  loading: boolean;
}) {
  if (loading || !overview) {
    return <div className="card text-slate-gray">Loading overview…</div>;
  }
  const completionPct = (overview.sessions.completionRate * 100).toFixed(0);
  const conversionPct = (overview.redemptions.conversionRate * 100).toFixed(0);
  const avgStars =
    overview.ratings.average !== null ? overview.ratings.average.toFixed(2) : '—';

  return (
    <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
      {/*
        The definition belongs in the label, not the secondary line. Written
        as "Unique devices · 28 filled the form · …" it read as though 28 were
        the device count, which is the one number on the card it isn't.
      */}
      <KpiCard
        label="Visitors (unique devices)"
        primary={overview.sessions.total.toLocaleString()}
        secondary={`${overview.sessions.withProfile.toLocaleString()} filled the form · ${overview.sessions.completed.toLocaleString()} completed the route (${completionPct}%)`}
      />
      <KpiCard
        label="POI QR scans"
        primary={overview.scans.poi.toLocaleString()}
        secondary={`${overview.scans.poiDevices.toLocaleString()} device${
          overview.scans.poiDevices === 1 ? '' : 's'
        } — a repeat scan of the same sticker counts again`}
      />
      <KpiCard
        label="Avg rating"
        primary={avgStars}
        secondary={`${overview.ratings.total.toLocaleString()} ratings`}
      />
      <KpiCard
        label="Suggestions"
        primary={overview.suggestions.total.toLocaleString()}
        secondary="Qualitative feedback rows"
      />
      <KpiCard
        label="Perks issued"
        primary={overview.redemptions.issued.toLocaleString()}
        secondary={`${overview.redemptions.expired.toLocaleString()} expired`}
      />
      <KpiCard
        label="Coupons scanned"
        primary={overview.redemptions.scans.toLocaleString()}
        secondary={`${overview.redemptions.scanned.toLocaleString()} coupons opened at least once`}
      />
      <KpiCard
        label="Perks redeemed"
        primary={overview.redemptions.redeemed.toLocaleString()}
        secondary={`${conversionPct}% conversion · €${formatBill(
          overview.redemptions.totalBillSum,
        )} total bills`}
      />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Entry funnel — the stages between scanning a sticker and doing a point
// ---------------------------------------------------------------------------
//
// Without this the dashboard jumps from "27 scans" to a Route progress block
// showing one visitor, and the reader is left wondering where the rest went.
// The answer is two drop-offs nobody could see: the mandatory demographics
// form, and everything between the form and the reward screen (story, quiz).

function EntryFunnel({
  overview,
  filter,
}: {
  overview?: Awaited<ReturnType<typeof api.analyticsOverview>>;
  filter: { from?: string; to?: string; villageId?: string };
}) {
  if (!overview) return null;

  const stages = [
    { label: 'Scanned a sticker', value: overview.sessions.total, hint: 'unique devices' },
    { label: 'Filled the form', value: overview.sessions.withProfile, hint: 'demographics submitted' },
    { label: 'Earned a badge', value: overview.sessions.withBadge, hint: 'finished at least one point' },
    { label: 'Completed the route', value: overview.sessions.completed, hint: 'reached the final point' },
  ];
  const head = stages[0]!.value;
  if (head === 0) return null;

  return (
    <section className="card">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-h2">Entry funnel</h2>
        {/* The file breaks the same stages down per village, which the block
            itself can't show without a row of small multiples. */}
        <a
          href={api.entryFunnelCsvUrl(filter)}
          className="btn-ghost text-tech-blue"
          download
        >
          ⬇ Export by village
        </a>
      </div>
      <p className="mt-1 text-caption text-slate-gray">
        Where visitors stop between scanning a QR sticker and doing a point.
        Percentages are of everyone who scanned.
      </p>
      <ol className="mt-4 space-y-2">
        {stages.map((stage, idx) => {
          const previous = idx > 0 ? stages[idx - 1]!.value : null;
          const drop =
            previous !== null && previous > 0
              ? ((previous - stage.value) / previous) * 100
              : null;
          const share = (stage.value / head) * 100;
          return (
            <li key={stage.label}>
              <div className="flex items-baseline justify-between gap-3 text-caption">
                <span className="text-dark-navy">
                  {stage.label}{' '}
                  <span className="text-slate-gray">— {stage.hint}</span>
                </span>
                <span className="whitespace-nowrap font-mono text-slate-gray">
                  {stage.value.toLocaleString()} · {share.toFixed(0)}%
                  {drop !== null && drop > 0 ? (
                    <span className="ml-2 text-error">▼ {drop.toFixed(0)}%</span>
                  ) : null}
                </span>
              </div>
              <div className="mt-1 h-2 overflow-hidden rounded-full bg-light-gray">
                <div
                  className="h-full rounded-full bg-tech-blue"
                  style={{ width: `${share}%` }}
                />
              </div>
            </li>
          );
        })}
      </ol>
      {/*
        Before 2 September the demographics form wasn't mandatory, so visitors
        could earn badges without ever appearing in stage 2. Counting them in
        stage 3 made it larger than stage 2 — not a funnel. They are named
        here instead of being silently dropped.
      */}
      {overview.sessions.withBadgeBeforeForm > 0 ? (
        <p className="mt-3 text-caption text-slate-gray">
          Plus {overview.sessions.withBadgeBeforeForm.toLocaleString()} visitor
          {overview.sessions.withBadgeBeforeForm === 1 ? '' : 's'} who earned a
          badge before the demographics form became mandatory (2 September) and
          so never passed through stage 2.
        </p>
      ) : null}
    </section>
  );
}

function KpiCard({
  label,
  primary,
  secondary,
}: {
  label: string;
  primary: string;
  secondary: string;
}) {
  return (
    <div className="card">
      <p className="text-caption uppercase tracking-wider text-slate-gray">{label}</p>
      <p className="mt-1 text-h1 text-dark-navy">{primary}</p>
      <p className="mt-1 text-caption text-slate-gray">{secondary}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Demographic card — horizontal-bar list, max 6 rows visible at once
// ---------------------------------------------------------------------------

function DemographicCard({
  title,
  buckets,
  emptyLabel,
  labelMap,
}: {
  title: string;
  buckets: { value: string | null; count: number }[];
  emptyLabel: string;
  // Optional slug→label map. When set, a bucket's raw value is shown via its
  // human label (falling back to the raw slug if unmapped). Used for the
  // "How did you hear about us?" breakdown whose values are stable slugs.
  labelMap?: Record<string, string>;
}) {
  const max = Math.max(1, ...buckets.map((b) => b.count));
  return (
    <div className="card">
      <h3 className="mb-3 text-h3 text-dark-navy">{title}</h3>
      {buckets.length === 0 ? (
        <p className="text-caption text-slate-gray">No data yet.</p>
      ) : (
        <ul className="space-y-2">
          {buckets.slice(0, 8).map((b, i) => (
            <li key={`${b.value ?? 'null'}-${i}`}>
              <div className="flex items-baseline justify-between text-caption">
                <span className={cn('text-dark-navy', !b.value && 'text-slate-gray/70')}>
                  {b.value ? labelMap?.[b.value] ?? b.value : emptyLabel}
                </span>
                <span className="font-mono text-slate-gray">{b.count}</span>
              </div>
              <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-light-gray">
                <div
                  className="h-full rounded-full bg-tech-blue"
                  style={{ width: `${(b.count / max) * 100}%` }}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Route progress — reach per POI, plus the real funnel inside it
// ---------------------------------------------------------------------------
//
// Two numbers per POI, because visitors do not all enter at point 1:
//   - reach       — everyone who earned the badge there, however they arrived;
//   - sequential  — of those who started at point 1, how many got this far.
//
// The old chart showed reach as "% of step 1", which produced step 2 at 150%
// of step 1 and read as a broken funnel. Only `sequential` is monotonic, so
// only it gets drop-off arrows.

function FunnelChart({
  route,
}: {
  route: Awaited<ReturnType<typeof api.analyticsFunnel>>['routes'][number];
}) {
  const maxReach = Math.max(1, ...route.locations.map((l) => l.visitors));
  const starters = route.startedAtFirst;

  return (
    <div className="card">
      <div className="mb-1 flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-h3 text-dark-navy">{route.name.en}</h3>
        {route.villageName?.en ? (
          <span className="text-caption uppercase tracking-wide text-slate-gray">
            {route.villageName.en}
          </span>
        ) : null}
      </div>
      <p className="mb-3 text-caption text-slate-gray">
        {starters.toLocaleString()} visitor{starters === 1 ? '' : 's'} started at
        point 1. Bars show everyone who reached each point; the second figure
        follows only those who started at the beginning.
      </p>
      {route.locations.length === 0 ? (
        <p className="text-caption text-slate-gray">No POIs configured.</p>
      ) : (
        <ol className="space-y-2">
          {route.locations.map((l, idx) => {
            const prevSequential =
              idx > 0 ? route.locations[idx - 1]!.sequential : null;
            const dropPct =
              prevSequential !== null && prevSequential > 0
                ? ((prevSequential - l.sequential) / prevSequential) * 100
                : null;
            const sequentialPct = starters > 0 ? (l.sequential / starters) * 100 : 0;
            return (
              <li key={l.id}>
                <div className="flex items-baseline justify-between gap-3 text-caption">
                  <span className="text-dark-navy">
                    <span className="mr-2 font-mono text-slate-gray">
                      {l.position}.
                    </span>
                    {l.title.en}
                  </span>
                  <span className="whitespace-nowrap font-mono text-slate-gray">
                    {l.visitors} reached
                    {' · '}
                    <span title="Visitors who started at point 1 and reached this point">
                      {l.sequential}/{starters} from the start ({sequentialPct.toFixed(0)}%)
                    </span>
                    {dropPct !== null && dropPct > 0 ? (
                      <span className="ml-2 text-error">
                        ▼ {dropPct.toFixed(0)}%
                      </span>
                    ) : null}
                  </span>
                </div>
                {/* Two stacked bars: the pale one is total reach, the solid
                    one the in-order subset, so the gap between "people who
                    walked past this sticker" and "people doing the route" is
                    visible rather than inferred. */}
                <div className="mt-1 h-2 overflow-hidden rounded-full bg-light-gray">
                  <div
                    className="h-full rounded-full bg-tech-blue/30"
                    style={{ width: `${(l.visitors / maxReach) * 100}%` }}
                  >
                    <div
                      className="h-full rounded-full bg-tech-blue"
                      style={{
                        width: l.visitors > 0 ? `${(l.sequential / l.visitors) * 100}%` : '0%',
                      }}
                    />
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Per-POI ratings table with distribution stripes
// ---------------------------------------------------------------------------

function RatingsTable({
  rows,
}: {
  rows: Awaited<ReturnType<typeof api.analyticsRatings>>['ratings'];
}) {
  return (
    <div className="overflow-x-auto rounded-card border border-neutral-gray bg-soft-white">
      <table className="w-full text-left text-body">
        <thead className="bg-light-gray text-caption uppercase text-slate-gray">
          <tr>
            <th className="px-4 py-2">Village</th>
            <th className="px-4 py-2 text-right">POI</th>
            <th className="px-4 py-2">Location</th>
            <th className="px-4 py-2 text-right">Count</th>
            <th className="px-4 py-2 text-right">Avg</th>
            <th className="px-4 py-2">Distribution</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const total = r.count || 1;
            const dist = r.distribution;
            return (
              <tr key={r.locationId ?? 'route'} className="border-t border-neutral-gray/60">
                <td className="px-4 py-2 text-caption uppercase tracking-wide text-slate-gray">
                  {r.location?.villageName?.en ?? '—'}
                </td>
                {/* The point's number in its route — the table is sorted by
                    rating volume, so the order here says nothing about it.
                    It doubles as the way into the POI editor: a low average
                    is usually followed by "so what's on that page?". */}
                <td className="px-4 py-2 text-right font-mono">
                  {r.location ? (
                    <Link
                      to="/locations/$locationId"
                      params={{ locationId: r.location.id }}
                      className="text-tech-blue underline-offset-2 hover:underline"
                      title={`Open ${r.location.title.en.trim()} in the editor`}
                    >
                      {r.location.position}
                    </Link>
                  ) : (
                    <span className="text-slate-gray">—</span>
                  )}
                </td>
                <td className="px-4 py-2 text-dark-navy">
                  {r.location?.title?.en ?? (
                    <span className="text-slate-gray">Route-level rating</span>
                  )}
                  {r.location?.shortId ? (
                    <span className="ml-2 font-mono text-caption text-slate-gray">
                      {r.location.shortId}
                    </span>
                  ) : null}
                </td>
                <td className="px-4 py-2 text-right font-mono">{r.count}</td>
                <td className="px-4 py-2 text-right font-mono">
                  {r.average !== null ? r.average.toFixed(2) : '—'}
                </td>
                <td className="px-4 py-2">
                  <div className="flex h-3 overflow-hidden rounded-full">
                    {(['5', '4', '3', '2', '1'] as const).map((star) => (
                      <span
                        key={star}
                        className={cn(
                          'inline-block h-full',
                          star === '5' && 'bg-success',
                          star === '4' && 'bg-success/60',
                          star === '3' && 'bg-warning',
                          star === '2' && 'bg-warning/60',
                          star === '1' && 'bg-error',
                        )}
                        style={{
                          width: `${((dist[star] || 0) / total) * 100}%`,
                        }}
                        title={`${star}★ — ${dist[star]}`}
                      />
                    ))}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Business + perk tables
// ---------------------------------------------------------------------------

function BusinessSummaryTable({
  rows,
}: {
  rows: Awaited<ReturnType<typeof api.analyticsRedemptionsSummary>>['byBusiness'];
}) {
  if (rows.length === 0) {
    return <div className="card text-slate-gray">No partner activity yet.</div>;
  }
  return (
    <div className="overflow-x-auto rounded-card border border-neutral-gray bg-soft-white">
      <table className="w-full text-left text-body">
        <thead className="bg-light-gray text-caption uppercase text-slate-gray">
          <tr>
            <th className="px-4 py-2">Business</th>
            <th className="px-4 py-2 text-right">Issued</th>
            <th className="px-4 py-2 text-right">Scans</th>
            <th className="px-4 py-2 text-right">Redeemed</th>
            <th className="px-4 py-2 text-right">Bills (€)</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((b, idx) => (
            <tr key={b.businessId ?? `null-${idx}`} className="border-t border-neutral-gray/60">
              <td className="px-4 py-2 text-dark-navy">
                {b.businessName ?? <span className="text-slate-gray/60">No partner</span>}
              </td>
              <td className="px-4 py-2 text-right font-mono">{b.issued}</td>
              <td className="px-4 py-2 text-right font-mono">{b.scans}</td>
              <td className="px-4 py-2 text-right font-mono">{b.redeemed}</td>
              <td className="px-4 py-2 text-right font-mono">
                {formatBill(b.totalBillSum)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PerkSummaryTable({
  rows,
}: {
  rows: Awaited<ReturnType<typeof api.analyticsRedemptionsSummary>>['byPerk'];
}) {
  if (rows.length === 0) {
    return <div className="card text-slate-gray">No perks issued yet.</div>;
  }
  return (
    <div className="overflow-x-auto rounded-card border border-neutral-gray bg-soft-white">
      <table className="w-full text-left text-body">
        <thead className="bg-light-gray text-caption uppercase text-slate-gray">
          <tr>
            <th className="px-4 py-2">Perk</th>
            <th className="px-4 py-2 text-right">Issued</th>
            <th className="px-4 py-2 text-right">Scans</th>
            <th className="px-4 py-2 text-right">Redeemed</th>
            <th className="px-4 py-2 text-right">Expired</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => (
            <tr key={p.perkId} className="border-t border-neutral-gray/60">
              <td className="px-4 py-2 text-dark-navy">
                {p.perkTitle?.en ?? <span className="text-slate-gray/60">Unknown</span>}
              </td>
              <td className="px-4 py-2 text-right font-mono">{p.issued}</td>
              <td className="px-4 py-2 text-right font-mono">{p.scans}</td>
              <td className="px-4 py-2 text-right font-mono">{p.redeemed}</td>
              <td className="px-4 py-2 text-right font-mono">{p.expired}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function formatBill(raw: string | null): string {
  if (!raw) return '0.00';
  const n = Number(raw);
  if (!Number.isFinite(n)) return raw;
  return n.toFixed(2);
}

// Preview block on the main dashboard — shows the 5 most recent suggestions
// for the current window. Full list lives at /analytics/suggestions and
// is reachable via the "View all" link above.
function LatestSuggestions({ range }: { range: DateRange }) {
  const q = useQuery({
    queryKey: ['analytics', 'suggestions', { ...range, limit: 5, offset: 0 }],
    queryFn: () => api.analyticsSuggestions({ ...range, limit: 5, offset: 0 }),
  });
  if (q.isLoading || !q.data) {
    return <div className="card text-slate-gray">Loading…</div>;
  }
  if (q.data.suggestions.length === 0) {
    return <div className="card text-slate-gray">No suggestions in this window.</div>;
  }
  return (
    <div className="space-y-3">
      {q.data.suggestions.map((s) => (
        <article key={s.id} className="card">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2 text-caption text-slate-gray">
            <span>
              {s.location ? (
                <span className="rounded-full bg-tech-blue/10 px-2 py-0.5 font-semibold text-tech-blue">
                  {s.location.title.en}
                </span>
              ) : (
                <span className="rounded-full bg-light-gray px-2 py-0.5">
                  Route-level
                </span>
              )}
            </span>
            <span>{new Date(s.createdAt).toLocaleString()}</span>
          </div>
          <p className="text-body text-dark-navy">{s.text}</p>
        </article>
      ))}
    </div>
  );
}
